import { useEffect, useState } from 'react';
import { supabase } from '../client/client';
import './EquipmentForm.css';
import Layout from './Layout';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const EquipmentForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [error, setError] = useState(null);
    const [attendantOptions, setAttendantOptions] = useState([]);

    const [formData, setFormData] = useState({
        active_type: '',
        image1: '',
        image2: '',
        area: '',
        brand: '',
        slug: '',
        model: '',
        serial: '',
        measures: '',
        color: '',
        state: '',
        tags: '',
        title: '',
        condition_use: '',
        quantity: 0,
        attendant: '',
        shipping_date: '',
        active_day: '',
    });

    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const areaOptions = [
        'ARBITRAJE', 'AUDITORIO', 'BAÑO AUDITORIO', 'BAÑO PERSONAL',
        'BAÑO SALON CERVESUR', 'CAJA', 'COCINA', 'COMERCIAL',
        'CONTABILIDAD', 'DIRECTORIO 3', 'GERENCIA', 'HALL PRIMERA CASONA',
        'HALL SEGUNDA CASONA', 'LEGAL', 'MENSAJERIA', 'ORIGEN',
        'PROTESTOS', 'SALON 1', 'SALON 2', 'SALON 3',
        'SALON CERVESUR', 'SALON VITOR', 'SECRETARIA', 'SUBGERENCIA',
        'TECNOLOGIA DE INFORMACION',
    ];

    // Load attendants from DB
    useEffect(() => {
        const fetchAttendants = async () => {
            try {
                const { data, error: dbError } = await supabase
                    .from('users')
                    .select('name')
                    .order('name', { ascending: true });
                if (!dbError && data) {
                    setAttendantOptions(data.map(u => u.name ? u.name.toUpperCase() : '').filter(Boolean));
                }
            } catch (e) {
                console.error('Error loading attendants:', e.message);
            }
        };
        fetchAttendants();
    }, []);

    useEffect(() => {
        if (!id) return;
        const fetchProductData = async () => {
            try {
                const { data, error: dbError } = await supabase
                    .from('equipos').select('*').eq('id', id).single();
                if (dbError) {
                    setError('Error al cargar el equipo.');
                } else {
                    setFormData({
                        active_type:   data.active_type   || '',
                        image1:        data.image1        || '',
                        image2:        data.image2        || '',
                        area:          data.area          || '',
                        brand:         data.brand         || '',
                        slug:          data.slug          || '',
                        model:         data.model         || '',
                        serial:        data.serial        || '',
                        measures:      data.measures      || '',
                        color:         data.color         || '',
                        state:         data.state         || '',
                        tags:          data.tags          || '',
                        title:         data.title         || '',
                        condition_use: data.condition_use || '',
                        quantity:      data.quantity      || 0,
                        attendant:     data.attendant     || '',
                        shipping_date: data.shipping_date || '',
                        active_day:    data.active_day    || '',
                    });
                }
            } catch (e) {
                setError('Error inesperado al cargar el equipo.');
            }
        };
        fetchProductData();
    }, [id]);

    const stateOptions = ['EN USO', 'NUEVO', 'OBSOLETO'];
    const yesNoOptions = ['SI', 'NO'];

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        // Skip toUpperCase for file inputs, date inputs, number inputs, and slug
        let formattedValue = value;
        if (type !== 'file' && type !== 'date' && type !== 'number' && name !== 'slug' && name !== 'tags') {
            formattedValue = formattedValue.toUpperCase();
        }

        if (type === 'file') return; // handled at submit time via e.target files

        if (name === 'title' || name === 'serial') {
            const newTitle  = name === 'title'  ? formattedValue : formData.title;
            const newSerial = name === 'serial' ? formattedValue : formData.serial;
            const slugTitle  = newTitle.toLowerCase().replace(/\s+/g, '_');
            const slugSerial = newSerial.toLowerCase().replace(/\s+/g, '_');
            setFormData((prev) => ({
                ...prev,
                [name]: formattedValue,
                slug: `${slugTitle}_${slugSerial}`,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: formattedValue,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const now = new Date().toISOString();
        let updatedData = { ...formData };

        try {
            if (e.target.image1.files && e.target.image1.files.length > 0) {
                const imageFile1 = e.target.image1.files[0];
                const imageName1 = uuidv4() + ".jpeg";
                const { error: uploadError1 } = await supabase.storage
                    .from('inventario_ccia').upload(imageName1, imageFile1);
                if (uploadError1) { setError('Error al subir imagen 1.'); return; }
                const { data: urlData1 } = supabase.storage.from('inventario_ccia').getPublicUrl(imageName1);
                updatedData = { ...updatedData, image1: urlData1.publicUrl };
            }

            if (e.target.image2.files && e.target.image2.files.length > 0) {
                const imageFile2 = e.target.image2.files[0];
                const imageName2 = uuidv4() + ".jpeg";
                const { error: uploadError2 } = await supabase.storage
                    .from('inventario_ccia').upload(imageName2, imageFile2);
                if (uploadError2) { setError('Error al subir imagen 2.'); return; }
                const { data: urlData2 } = supabase.storage.from('inventario_ccia').getPublicUrl(imageName2);
                updatedData = { ...updatedData, image2: urlData2.publicUrl };
            }

            if (id) {
                const { error: updateError } = await supabase.from('equipos')
                    .update({ updated_at: now, ...updatedData }).eq('id', id);
                if (updateError) { setError('Error al actualizar el equipo.'); return; }
            } else {
                const { error: insertError } = await supabase.from('equipos').insert({ ...updatedData });
                if (insertError) { setError('Error al registrar el equipo.'); return; }
            }

            setFormData(updatedData);
            setShowSuccessDialog(true);
        } catch (e) {
            setError('Error inesperado al guardar.');
        }
    };

    const handleAccept = () => {
        setShowSuccessDialog(false);
        navigate("/equipos");
    };

    return (
        <Layout>
            <form className="equipment-form" onSubmit={handleSubmit}>
                <h1 className='text-3xl font-bold text-[#4691A6] flex justify-center pb-10'>HARDWARE</h1>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                <div className="form-row">
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Tipo de Activo:
                            <input type="text" name="active_type" value={formData.active_type} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Nombre del Equipo:
                            <input type="text" name="title" value={formData.title} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Cantidad:
                            <input type="number" name="quantity" value={formData.quantity} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Marca:
                            <input type="text" name="brand" value={formData.brand} onChange={handleChange} />
                        </label>
                    </div>
                </div>

                <div className="form-row">
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Modelo:
                            <input type="text" name="model" value={formData.model} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Número de Serie:
                            <input type="text" name="serial" value={formData.serial} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Medidas:
                            <input type="text" name="measures" value={formData.measures} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Color:
                            <input type="text" name="color" value={formData.color} onChange={handleChange} />
                        </label>
                    </div>
                </div>

                <div className="form-row">
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Estado:
                            <div className="styled-select">
                                <select name="state" value={formData.state} onChange={handleChange} className="styled-select">
                                    <option value="">Selecciona una opción</option>
                                    {stateOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Condición de Uso:
                            <div className="styled-select">
                                <select name="condition_use" value={formData.condition_use} onChange={handleChange} className="styled-select">
                                    <option value="">Selecciona una opción</option>
                                    {yesNoOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Encargado del Equipo:
                            <div className="styled-select">
                                <select name="attendant" value={formData.attendant} onChange={handleChange}>
                                    <option value="">Selecciona una opción</option>
                                    {attendantOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Área:
                            <div className="styled-select">
                                <select name="area" value={formData.area} onChange={handleChange} className="styled-select">
                                    <option value="">Selecciona una opción</option>
                                    {areaOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </label>
                    </div>
                </div>

                <div className="form-row">
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Tags (etiquetas):
                            <input
                                type="text"
                                name="tags"
                                value={formData.tags}
                                onChange={handleChange}
                                placeholder="ej: laptop,dell,sistemas"
                            />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Fecha de Despacho:
                            <input type="date" name="shipping_date" value={formData.shipping_date} onChange={handleChange} />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Fecha de Activación:
                            <input type="date" name="active_day" value={formData.active_day} onChange={handleChange} />
                        </label>
                    </div>
                </div>

                <div className="form-row pb-10">
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Slug (auto):
                            <input type="text" name="slug" value={formData.slug} readOnly />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Imagen 1:
                            <input type="file" name="image1" accept="image/*" />
                        </label>
                    </div>
                    <div className="field">
                        <label className='text-xs font-semibold'>
                            Imagen 2:
                            <input type="file" name="image2" accept="image/*" />
                        </label>
                    </div>
                </div>

                <div className="form-row">
                    <button
                        className="bg-[#356674] hover:bg-[#4691A6] transition-colors duration-300 text-base cursor-pointer mt-7 text-white font-semibold py-2 px-4 rounded-md w-full"
                        type="submit"
                    >
                        Guardar
                    </button>
                </div>

                {showSuccessDialog && (
                    <div className="success-dialog">
                        <p>Se ha guardado exitosamente</p>
                        <button
                            onClick={handleAccept}
                            className="bg-[#356674] hover:bg-[#4691A6] transition-colors duration-300 text-base cursor-pointer mt-7 text-white font-semibold py-2 px-4 rounded-md w-full"
                        >
                            Aceptar
                        </button>
                    </div>
                )}
            </form>
        </Layout>
    );
};

export default EquipmentForm;
